import { useCallback, useEffect, useRef, useState } from 'react';

export interface PanelPosition {
  x: number;
  y: number;
}

interface PanelSize {
  width: number;
  height: number;
}

/** Keeps a little of the panel on screen rather than letting it be dragged out of reach. */
const VIEWPORT_MARGIN = 8;

/** How far an arrow key nudges the panel, so it can be placed without a mouse. */
const KEYBOARD_STEP = 16;
const KEYBOARD_STEP_FINE = 2;

function clampToViewport(
  position: PanelPosition,
  size: PanelSize
): PanelPosition {
  const maxX = Math.max(
    VIEWPORT_MARGIN,
    window.innerWidth - size.width - VIEWPORT_MARGIN
  );
  const maxY = Math.max(
    VIEWPORT_MARGIN,
    window.innerHeight - size.height - VIEWPORT_MARGIN
  );

  return {
    x: Math.min(Math.max(position.x, VIEWPORT_MARGIN), maxX),
    y: Math.min(Math.max(position.y, VIEWPORT_MARGIN), maxY)
  };
}

function readStoredPosition(storageKey: string): PanelPosition | null {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (typeof parsed?.x !== 'number' || typeof parsed?.y !== 'number') {
      return null;
    }

    return { x: parsed.x, y: parsed.y };
  } catch {
    // Private windows and blocked site data both throw here. A panel that cannot remember
    // where it was put is a far smaller problem than one that refuses to render.
    return null;
  }
}

/**
 * Makes a fixed-position panel draggable, remembering where it was left.
 *
 * Until it is moved the panel has no stored position and the caller is expected to centre it with
 * CSS, so nothing has to be measured before the first paint. The first drag converts that centred
 * position into real coordinates by reading the rendered box.
 */
export function useDraggablePanel(storageKey: string) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<PanelPosition | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Restored on mount rather than in useState, so a stored position saved on a larger screen is
  // pulled back into view instead of leaving the panel off the edge.
  useEffect(() => {
    const stored = readStoredPosition(storageKey);
    if (!stored) return;

    const rect = panelRef.current?.getBoundingClientRect();
    setPosition(
      clampToViewport(stored, {
        width: rect?.width ?? 0,
        height: rect?.height ?? 0
      })
    );
  }, [storageKey]);

  useEffect(() => {
    if (!position) return;

    try {
      window.localStorage.setItem(storageKey, JSON.stringify(position));
    } catch {
      // Storage being unavailable must not break dragging.
    }
  }, [position, storageKey]);

  // A panel parked against the right or bottom edge would otherwise be stranded off screen when
  // the window shrinks.
  useEffect(() => {
    const handleResize = () => {
      const rect = panelRef.current?.getBoundingClientRect();
      if (!rect) return;

      setPosition((current) =>
        current
          ? clampToViewport(current, {
              width: rect.width,
              height: rect.height
            })
          : current
      );
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const panel = panelRef.current;
      if (!panel) return;

      // Stops the drag selecting text in the page behind the panel.
      event.preventDefault();

      const rect = panel.getBoundingClientRect();
      const size = { width: rect.width, height: rect.height };
      const grabX = event.clientX - rect.left;
      const grabY = event.clientY - rect.top;

      setIsDragging(true);

      const handleMove = (moveEvent: PointerEvent) => {
        setPosition(
          clampToViewport(
            { x: moveEvent.clientX - grabX, y: moveEvent.clientY - grabY },
            size
          )
        );
      };

      const handleUp = () => {
        setIsDragging(false);
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        window.removeEventListener('pointercancel', handleUp);
      };

      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
      window.addEventListener('pointercancel', handleUp);
    },
    []
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      const offsets: Record<string, PanelPosition> = {
        ArrowLeft: { x: -1, y: 0 },
        ArrowRight: { x: 1, y: 0 },
        ArrowUp: { x: 0, y: -1 },
        ArrowDown: { x: 0, y: 1 }
      };
      const offset = offsets[event.key];
      if (!offset) return;

      const panel = panelRef.current;
      if (!panel) return;

      event.preventDefault();
      const rect = panel.getBoundingClientRect();
      const step = event.shiftKey ? KEYBOARD_STEP_FINE : KEYBOARD_STEP;

      setPosition(
        clampToViewport(
          { x: rect.left + offset.x * step, y: rect.top + offset.y * step },
          { width: rect.width, height: rect.height }
        )
      );
    },
    []
  );

  const resetPosition = useCallback(() => {
    setPosition(null);
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      // Nothing to recover from: the panel is already back in the centre.
    }
  }, [storageKey]);

  return {
    panelRef,
    position,
    isDragging,
    handlePointerDown,
    handleKeyDown,
    resetPosition
  };
}
